import isEqual from 'lodash/isEqual';
import {
    BooleanAttribute,
    NumberAttribute,
    WatermarkAttribute,
    getAttributesToSerialize
} from '../attributes';

import { WatermarkPositions } from '../../enums';
import { TimeInvariantBase } from '../TimeInvariantBase';

import { Config } from '../../video-data/config';
import type { MediaSourceData } from '../asset/MediaAsset';
import type { WatermarkThemeRef } from '../attributes/template/common';
import { assertArray, assertInArray, assertMinimumLength } from '../../validators';
import type { Template } from '../template/Template';
import type { FillMode } from '../../video-data/types';
import { WatermarkLayout } from './WatermarkLayout';

/**
 * This class contains all watermark settings that can be set on the template level.
 * These settings can be overridden by a scene using its SceneWatermarkSettings.
 */
export class TemplateWatermarkManager extends TimeInvariantBase {
    override _classType: string = 'TemplateWatermarkManager';
    override name = 'TemplateWatermarkManager';

    #position: WatermarkPositions = WatermarkPositions.TopRight;
    @WatermarkAttribute()
    sourceData: MediaSourceData | typeof Config.WATERMARK_NONE | WatermarkThemeRef = Config.WATERMARK_NONE;
    @NumberAttribute({ min: 0, max: 1 })
    opacity = 1;
    @BooleanAttribute()
    visible = true;
    @BooleanAttribute()
    isEditable = true;

    // Watermark media fill mode
    #fillMode: FillMode = Config.FILL_AREA;

    /**
     * Contains watermark margins & max size for landscape mode.
     */
    landscapeLayout = new WatermarkLayout({ parent: this, fetchResources: false });
    /**
     * Contains watermark margins & max size for vertical mode.
     */
    verticalLayout = new WatermarkLayout({ parent: this, fetchResources: false });
    /**
     * Contains watermark margins & max size for square mode.
     */
    squareLayout = new WatermarkLayout({ parent: this, fetchResources: false });

    #availablePositions: WatermarkPositions[] = Config.WATERMARK_POSITIONS;

    /**
     * The position (corner) of the watermark
     */
    get position(): WatermarkPositions {
        return this.#position;
    }

    set position(position: WatermarkPositions) {
        if (this.#position === position) {
            return;
        }
        assertInArray(position, this.availablePositions);
        this.#position = position;
        this.requiresUpdate = true;
    }

    /**
     * List of allowed watermark position for all scenes in video/template
     */
    get availablePositions(): WatermarkPositions[] {
        return this.#availablePositions;
    }

    set availablePositions(availablePositions: WatermarkPositions[]) {
        if (isEqual(this.#availablePositions, availablePositions)) {
            return;
        }
        assertArray(availablePositions);
        assertMinimumLength(availablePositions, 1);
        assertInArray(this.position, availablePositions);
        availablePositions.forEach(position => assertInArray(position, Config.WATERMARK_POSITIONS));
        this.#availablePositions = availablePositions;
        this.requiresUpdate = true;
    }

    _availablePositionsCustomLoad(positions: WatermarkPositions[]) {
        // Custom load is required as default position is `top_right`, and the `set availablePositions()` will have
        // failing assertions during load if the `availablePositions` does not include the default `top_right`.
        this.#availablePositions = positions;
    }

    /**
     * Gets the layout (margins & watermark max size) applicable to the current aspect ratio.
     */
    get activeLayout(): WatermarkLayout {
        switch (this.root.aspectRatio) {
            case Config.LANDSCAPE_ASPECT_RATIO:
                return this.landscapeLayout;
            case Config.SQUARE_ASPECT_RATIO:
                return this.squareLayout;
            case Config.VERTICAL_ASPECT_RATIO:
                return this.verticalLayout;
            default:
                throw new Error('Aspect ratio is not supported');
        }
    }

    set fillMode(value) {
        if (value === this.fillMode) {
            return;
        }
        assertInArray(value, Config.ALL_FILL_MODES);
        this.#fillMode = value;
        this.requiresUpdate = true;
    }

    get fillMode() {
        return this.#fillMode;
    }

    override clear() {
        super.clear();
        this.sourceData = Config.WATERMARK_NONE;
        this.availablePositions = Config.WATERMARK_POSITIONS;
        this.position = Config.WATERMARK_TOP_RIGHT_POSITION;
        this.opacity = Config.WATERMARK_DEFAULT_OPACITY;
        // Layout:
        // We don't clear the layout, since users can't change these
        // and we don't want to lose to the template's values.
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat([
                'availablePositions',
                'landscapeLayout',
                'verticalLayout',
                'squareLayout',
                'position',
                'fillMode',
            ]).concat(getAttributesToSerialize(this));
    }

    override get template(): Template {
        return this.parent as Template;
    }
}
